import { useCallback, useEffect, useRef } from 'react';
import type { SequenceProvider } from '@/types/custom-playbooks';
import type {
  SequencerConnection,
  SequencerSequence,
  SequencerUser,
} from '@/types/sequencers';
import { gtmAgentsApi } from './gtm-agents.feature';

/**
 * Outside sequencers for the Add to sequence step: cleanse-api
 * `api/agents/sequencers.py`, behind `require_gtm_agents`.
 * Callers skip every query while Agents access is locked or loading.
 *
 * A Salesloft, Nooks or Instantly key is handed to the request through a
 * pending map, as the org secrets do, so Redux actions, caches and devtools only ever see the
 * request id, never the key.
 */
type OutsideProvider = Exclude<SequenceProvider, 'cleanlist'>;
/** The sequencers that connect with an API key on these routes (lemlist
 *  keeps its own, `/crm/lemlist/connect`). */
export type KeyProvider = 'salesloft' | 'nooks' | 'instantly';
const headers = (orgId: string) => ({ 'X-Cleanlist-Expected-Org': orgId });
const pendingKeys = new Map<string, { orgId: string; apiKey: string }>();

export const sequencersApi = gtmAgentsApi
  .enhanceEndpoints({
    addTagTypes: [
      'SequencerConnection',
      'SequencerSequences',
      'SequencerUsers',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      listSequencerConnections: builder.query<
        SequencerConnection[],
        { orgId: string }
      >({
        query: ({ orgId }) => ({
          url: '/sequencers/connections',
          headers: headers(orgId),
        }),
        providesTags: (_result, _error, { orgId }) => [
          { type: 'SequencerConnection', id: orgId },
        ],
      }),
      listSequencerSequences: builder.query<
        SequencerSequence[],
        { orgId: string; provider: OutsideProvider }
      >({
        query: ({ orgId, provider }) => ({
          url: `/sequencers/${provider}/sequences`,
          headers: headers(orgId),
        }),
        providesTags: (_result, _error, { orgId, provider }) => [
          { type: 'SequencerSequences', id: `${orgId}:${provider}` },
        ],
      }),
      listSequencerUsers: builder.query<
        SequencerUser[],
        { orgId: string; provider: OutsideProvider }
      >({
        query: ({ orgId, provider }) => ({
          url: `/sequencers/${provider}/users`,
          headers: headers(orgId),
        }),
        providesTags: (_result, _error, { orgId, provider }) => [
          { type: 'SequencerUsers', id: `${orgId}:${provider}` },
        ],
      }),
      submitSequencerKey: builder.mutation<
        SequencerConnection,
        { orgId: string; provider: KeyProvider; requestId: string }
      >({
        async queryFn(
          { orgId, provider, requestId },
          _api,
          _options,
          baseQuery
        ) {
          const pending = pendingKeys.get(requestId);
          pendingKeys.delete(requestId);
          if (!pending || pending.orgId !== orgId)
            return {
              error: { status: 'CUSTOM_ERROR', error: 'Key request expired.' },
            };
          const result = await baseQuery({
            url: `/integrations/${provider}/api-key`,
            method: 'POST',
            headers: headers(orgId),
            body: { apiKey: pending.apiKey },
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as SequencerConnection };
        },
        invalidatesTags: (_result, error, { orgId, provider }) =>
          error
            ? []
            : [
                { type: 'SequencerConnection', id: orgId },
                { type: 'SequencerSequences', id: `${orgId}:${provider}` },
                { type: 'SequencerUsers', id: `${orgId}:${provider}` },
              ],
      }),
      disconnectSequencer: builder.mutation<
        SequencerConnection,
        { orgId: string; provider: Exclude<OutsideProvider, 'lemlist'> }
      >({
        query: ({ orgId, provider }) => ({
          url: `/integrations/${provider}`,
          method: 'DELETE',
          headers: headers(orgId),
        }),
        invalidatesTags: (_result, error, { orgId, provider }) =>
          error
            ? []
            : [
                { type: 'SequencerConnection', id: orgId },
                { type: 'SequencerSequences', id: `${orgId}:${provider}` },
                { type: 'SequencerUsers', id: `${orgId}:${provider}` },
              ],
      }),
      startOutreachConnect: builder.mutation<
        { url: string },
        { orgId: string }
      >({
        query: ({ orgId }) => ({
          url: '/integrations/outreach/oauth/start',
          method: 'POST',
          headers: headers(orgId),
        }),
      }),
    }),
  });

export const {
  useDisconnectSequencerMutation,
  useListSequencerConnectionsQuery,
  useListSequencerSequencesQuery,
  useListSequencerUsersQuery,
  useStartOutreachConnectMutation,
} = sequencersApi;

/** Connect Salesloft, Nooks or Instantly with an API key, the key kept out
 *  of Redux. */
export function useConnectSequencerKeyMutation(
  orgId: string,
  provider: KeyProvider
) {
  const [submit, state] = sequencersApi.useSubmitSequencerKeyMutation();
  const pendingIds = useRef(new Set<string>());
  useEffect(() => {
    const ids = pendingIds.current;
    return () => {
      for (const id of ids) pendingKeys.delete(id);
      ids.clear();
    };
  }, [orgId]);
  const connect = useCallback(
    (apiKey: string) => {
      const requestId = crypto.randomUUID();
      pendingIds.current.add(requestId);
      pendingKeys.set(requestId, { orgId, apiKey });
      try {
        const request = submit({ orgId, provider, requestId });
        void request.finally(() => {
          pendingKeys.delete(requestId);
          pendingIds.current.delete(requestId);
        });
        return request;
      } catch (error) {
        pendingKeys.delete(requestId);
        pendingIds.current.delete(requestId);
        throw error;
      }
    },
    [orgId, provider, submit]
  );
  return [connect, state] as const;
}
